import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API_URL, formatUSD, formatDate, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Pencil, Trash2, Search, Printer, X, FileText, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Calculator } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TransactionDialog from "@/components/TransactionDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import DonorQuickView from "@/components/DonorQuickView";
import TxnQuickView from "@/components/TxnQuickView";
import { toast } from "sonner";

const PAGE_SIZE_OPTIONS = [25, 50, 100, "All"];

export default function LedgerPage() {
  const { activeOrgId } = useAuth();
  const [searchParams] = useSearchParams();
  const [txns, setTxns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [donors, setDonors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters — seed from URL query params so drilldowns from other pages land pre-filtered.
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [fundFilter, setFundFilter] = useState("all");
  const [startDate, setStartDate] = useState(searchParams.get("start") || "");
  const [endDate, setEndDate] = useState(searchParams.get("end") || "");
  const [includeAutoFees, setIncludeAutoFees] = useState(false);

  // Pagination
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDefaultType, setDialogDefaultType] = useState("income");
  const [editing, setEditing] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [printing, setPrinting] = useState(false);

  // Quick-view drilldowns
  const [donorQV, setDonorQV] = useState(null); // donor_id string
  const [txnQV, setTxnQV] = useState(null);     // txn object

  // Fetch transactions whenever server-side filters change. Search is applied
  // client-side so the table reacts instantly while typing.
  const fetchTransactions = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const params = { limit: 5000 };
      if (typeFilter !== "all") params.type = typeFilter;
      if (categoryFilter !== "all") params.category_id = categoryFilter;
      if (fundFilter !== "all") params.fund_id = fundFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (includeAutoFees) params.include_auto_fees = true;
      const { data } = await api.get(`/organizations/${activeOrgId}/transactions`, { params });
      setTxns(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    if (!activeOrgId) return;
    const [cRes, dRes, fRes, vRes, oRes] = await Promise.all([
      api.get(`/organizations/${activeOrgId}/categories`),
      api.get(`/organizations/${activeOrgId}/donors`),
      api.get(`/organizations/${activeOrgId}/funds`),
      api.get(`/organizations/${activeOrgId}/vendors`),
      api.get(`/organizations/${activeOrgId}/donation-origins`),
    ]);
    setCategories(cRes.data);
    setDonors(dRes.data);
    setFunds(fRes.data);
    setVendors(vRes.data);
    setOrigins(oRes.data);
  };

  useEffect(() => { fetchLookups(); /* eslint-disable-next-line */ }, [activeOrgId]);
  useEffect(() => {
    fetchTransactions();
    /* eslint-disable-next-line */
  }, [activeOrgId, typeFilter, categoryFilter, fundFilter, startDate, endDate, includeAutoFees]);

  // Filter setters that also reset pagination so the user always lands on the
  // first page after narrowing or widening the result set.
  const updateType = (v) => { setTypeFilter(v); setPage(1); };
  const updateCategory = (v) => { setCategoryFilter(v); setPage(1); };
  const updateFund = (v) => { setFundFilter(v); setPage(1); };
  const updateStart = (v) => { setStartDate(v); setPage(1); };
  const updateEnd = (v) => { setEndDate(v); setPage(1); };
  const updateAutoFees = (v) => { setIncludeAutoFees(v); setPage(1); };
  const updateSearch = (v) => { setSearch(v); setPage(1); };

  // Client-side global search across description / donor / category / fund / reference.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return txns;
    return txns.filter((t) =>
      (t.description || "").toLowerCase().includes(needle)
      || (t.donor_name || "").toLowerCase().includes(needle)
      || (t.category_name || "").toLowerCase().includes(needle)
      || (t.fund_name || "").toLowerCase().includes(needle)
      || (t.reference || "").toLowerCase().includes(needle)
    );
  }, [txns, search]);

  // Summary cards: derived from the *filtered* set so the numbers always match
  // what the user is looking at on screen.
  const totals = useMemo(() => {
    let inc = 0, exp = 0;
    for (const t of filtered) {
      if (t.type === "income") inc += Number(t.amount) - Number(t.fee || 0);
      else exp += Number(t.amount);
    }
    return { inc, exp, net: inc - exp };
  }, [filtered]);

  // Category / fund breakdowns for the total-card drilldown popover.
  const breakdowns = useMemo(() => {
    const build = (rows, byField) => {
      const map = new Map();
      for (const t of rows) {
        const key = t[byField] || "Uncategorized";
        const val = t.type === "income" ? Number(t.amount) - Number(t.fee || 0) : Number(t.amount);
        map.set(key, (map.get(key) || 0) + val);
      }
      return [...map.entries()].map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 5);
    };
    const inc = filtered.filter(t => t.type === "income");
    const exp = filtered.filter(t => t.type === "expense");
    return {
      incomeByCategory: build(inc, "category_name"),
      incomeByFund: build(inc, "fund_name"),
      expenseByCategory: build(exp, "category_name"),
    };
  }, [filtered]);

  // Pagination
  const totalPages = pageSize === "All" ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    if (pageSize === "All") return filtered;
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const hasActiveFilter = search || typeFilter !== "all" || categoryFilter !== "all"
    || fundFilter !== "all" || startDate || endDate || includeAutoFees;

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setCategoryFilter("all");
    setFundFilter("all");
    setStartDate("");
    setEndDate("");
    setIncludeAutoFees(false);
  };

  const doDelete = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/transactions/${confirmTarget.id}`);
      toast.success("Transaction deleted");
      setConfirmTarget(null);
      fetchTransactions();
    } catch (e) { toast.error(formatApiError(e)); setConfirmTarget(null); }
  };

  const printToPDF = async () => {
    if (!activeOrgId) return;
    setPrinting(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.set("type", typeFilter);
      if (categoryFilter !== "all") params.set("category_id", categoryFilter);
      if (fundFilter !== "all") params.set("fund_id", fundFilter);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (search.trim()) params.set("search", search.trim());
      if (includeAutoFees) params.set("include_auto_fees", "true");
      const token = localStorage.getItem("ledger_token");
      const res = await fetch(
        `${API_URL}/organizations/${activeOrgId}/reports/ledger.pdf?${params.toString()}`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ledger-${startDate || "all"}_to_${endDate || "today"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Ledger PDF downloaded");
    } catch (e) {
      toast.error(e?.message || "Could not generate PDF");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Ledger</div>
          <h1 className="font-serif text-5xl tracking-tight mt-2">All transactions</h1>
          <p className="text-muted-foreground mt-2 max-w-md">
            The single source of truth for every dollar in and out.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="ledger-print-pdf-button"
            onClick={printToPDF}
            disabled={printing}
            variant="outline"
            className="h-11 rounded-2xl active:scale-[0.98]"
          >
            {printing ? <FileText className="w-4 h-4 mr-2 animate-pulse" /> : <Printer className="w-4 h-4 mr-2" />}
            {printing ? "Generating…" : "Print to PDF"}
          </Button>
          <Button
            data-testid="add-donation-button"
            onClick={() => { setEditing(null); setDialogDefaultType("income"); setDialogOpen(true); }}
            className="h-11 rounded-2xl soft-glow active:scale-[0.98]"
          >
            <Plus className="w-4 h-4 mr-2" /> Record Donation
          </Button>
          <Button
            data-testid="add-expense-button"
            onClick={() => { setEditing(null); setDialogDefaultType("expense"); setDialogOpen(true); }}
            variant="outline"
            className="h-11 rounded-2xl active:scale-[0.98]"
          >
            <Plus className="w-4 h-4 mr-2" /> Record Expense
          </Button>
        </div>
      </header>

      {/* Dynamic summary cards — recalculate from the filtered view. Click to drill. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <BreakdownCard
          testid="ledger-total-income"
          label="Total income"
          value={totals.inc}
          valueClass="text-primary"
          subline={`${filtered.filter(t => t.type === "income").length} donations · net of fees`}
          rows={breakdowns.incomeByCategory}
          altRows={breakdowns.incomeByFund}
          altLabel="By fund"
          rowLabel="By category"
          empty="No income in this view"
        />
        <BreakdownCard
          testid="ledger-total-expense"
          label="Total expenses"
          value={totals.exp}
          valueClass="text-expense"
          subline={`${filtered.filter(t => t.type === "expense").length} expenses`}
          rows={breakdowns.expenseByCategory}
          rowLabel="By category"
          empty="No expenses in this view"
        />
        <Card className="p-5 border-0 shadow-sm bg-card rounded-3xl" data-testid="ledger-net-card">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Net</div>
          <div className={`font-serif text-3xl mt-2 tabular-nums ${totals.net >= 0 ? "text-primary" : "text-destructive"}`} data-testid="ledger-net">{formatUSD(totals.net)}</div>
          <div className="text-xs text-muted-foreground mt-1">{hasActiveFilter ? "filtered view" : "all time"}</div>
        </Card>
      </div>

      <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
        {/* Filter bar */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="ledger-search-input"
                value={search}
                onChange={(e) => updateSearch(e.target.value)}
                placeholder="Search description, donor, category, fund…"
                className="pl-9 h-10 rounded-xl"
              />
            </div>
            <Select value={typeFilter} onValueChange={updateType}>
              <SelectTrigger data-testid="ledger-filter-type" className="w-36 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v)}>
              <SelectTrigger data-testid="ledger-filter-category" className="w-48 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fundFilter} onValueChange={updateFund}>
              <SelectTrigger data-testid="ledger-filter-fund" className="w-44 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All funds</SelectItem>
                {funds.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">From</span>
              <Input
                type="date"
                data-testid="ledger-start-date"
                value={startDate}
                onChange={(e) => updateStart(e.target.value)}
                className="h-10 rounded-xl w-[150px]"
              />
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">to</span>
              <Input
                type="date"
                data-testid="ledger-end-date"
                value={endDate}
                onChange={(e) => updateEnd(e.target.value)}
                className="h-10 rounded-xl w-[150px]"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground ml-1">
              <Switch
                checked={includeAutoFees}
                onCheckedChange={updateAutoFees}
                data-testid="ledger-toggle-autofees"
              />
              Show auto-generated fee rows
            </label>
            {hasActiveFilter && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                className="ml-auto h-9 rounded-xl text-muted-foreground"
                data-testid="ledger-clear-filters"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear filters
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Fund</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium">Donor / Vendor</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">Date Deposited</th>
                <th className="px-4 py-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody data-testid="ledger-table-body">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">
                  {hasActiveFilter ? "No transactions match your filters." : "No transactions yet."}
                </td></tr>
              ) : (
                <TooltipProvider delayDuration={150}>
                  {paged.map((t) => {
                    const fee = Number(t.fee || 0);
                    const isIncome = t.type === "income";
                    const grossAmt = Number(t.amount);
                    const netAmt = isIncome ? grossAmt - fee : grossAmt;
                    const showFeeTooltip = isIncome && fee > 0;
                    return (
                      <tr key={t.id} data-testid={`txn-row-${t.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap">
                          <button
                            className="hover:text-primary hover:underline underline-offset-2 transition-colors"
                            onClick={() => { updateStart(t.date); updateEnd(t.date); }}
                            data-testid={`txn-date-${t.id}`}
                            title="Filter to this date"
                          >
                            {formatDate(t.date)}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={isIncome ? "bg-primary/10 text-primary border-primary/20 rounded-full" : "bg-expense/10 text-expense border-expense/20 rounded-full"}
                          >
                            {t.type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {t.category_name ? (
                            <button
                              className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                              onClick={() => updateCategory(t.category_id)}
                              data-testid={`txn-category-${t.id}`}
                              title="Filter by this category"
                            >
                              {t.category_name}
                            </button>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-foreground/70 text-xs">
                          {t.fund_name ? (
                            <button
                              className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                              onClick={() => updateFund(t.fund_id)}
                              data-testid={`txn-fund-${t.id}`}
                              title="Filter by this fund"
                            >
                              {t.fund_name}
                            </button>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-foreground/80 max-w-xs truncate">
                          <button
                            className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left truncate w-full"
                            onClick={() => setTxnQV(t)}
                            data-testid={`txn-description-${t.id}`}
                            title="See full transaction detail"
                          >
                            {t.description || "—"}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-foreground/80">
                          {isIncome ? (
                            t.donor_id ? (
                              <button
                                className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                                onClick={() => setDonorQV(t.donor_id)}
                                data-testid={`txn-donor-${t.id}`}
                                title="View donor profile"
                              >
                                {t.donor_name}
                              </button>
                            ) : (t.donor_name || "—")
                          ) : (t.vendor_name || t.donor_name || "—")}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono tabular-nums whitespace-nowrap ${isIncome ? "text-primary" : "text-expense"}`}>
                          {showFeeTooltip ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">
                                  +{formatUSD(netAmt)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="rounded-xl text-xs">
                                <div>Gross: <span className="tabular-nums">{formatUSD(grossAmt)}</span></div>
                                <div>Fees: <span className="tabular-nums">-{formatUSD(fee)}</span></div>
                                <div className="font-semibold mt-1">Net: <span className="tabular-nums">{formatUSD(netAmt)}</span></div>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <>{isIncome ? "+" : "−"}{formatUSD(grossAmt)}</>
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-foreground/70 whitespace-nowrap" data-testid={`txn-date-deposited-${t.id}`}>
                          {t.date_deposited ? formatDate(t.date_deposited) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`txn-row-menu-${t.id}`}>•••</Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => { setEditing(t); setDialogOpen(true); }} data-testid={`txn-edit-${t.id}`}>
                                <Pencil className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive" onSelect={() => setConfirmTarget(t)} data-testid={`txn-delete-${t.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </TooltipProvider>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border bg-secondary/30">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => { setPageSize(v === "All" ? "All" : Number(v)); setPage(1); }}
            >
              <SelectTrigger className="h-8 w-20 rounded-lg text-xs" data-testid="ledger-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <span data-testid="ledger-result-count" className="ml-2">
              {filtered.length === 0 ? "0 results"
                : pageSize === "All"
                  ? `Showing all ${filtered.length} result${filtered.length === 1 ? "" : "s"}`
                  : `Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, filtered.length)} of ${filtered.length}`}
            </span>
          </div>
          {pageSize !== "All" && totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost" size="sm" className="h-8 rounded-lg"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="ledger-page-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-muted-foreground px-2 tabular-nums" data-testid="ledger-page-indicator">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost" size="sm" className="h-8 rounded-lg"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="ledger-page-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>

      <TransactionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={activeOrgId}
        txn={editing}
        categories={categories}
        donors={donors}
        funds={funds}
        vendors={vendors}
        origins={origins}
        defaultType={dialogDefaultType}
        onSaved={fetchTransactions}
        onLookupsChanged={() => { fetchLookups(); fetchTransactions(); }}
      />

      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title="Delete this transaction?"
        description={confirmTarget ? `${confirmTarget.type === "income" ? "Income" : "Expense"} of ${formatUSD(confirmTarget.amount)} on ${confirmTarget.date}. This cannot be undone.` : ""}
        confirmLabel="Delete transaction"
        onConfirm={doDelete}
        testid="confirm-delete-transaction"
      />

      <DonorQuickView
        donorId={donorQV}
        open={!!donorQV}
        onOpenChange={(o) => { if (!o) setDonorQV(null); }}
      />
      <TxnQuickView
        txn={txnQV}
        open={!!txnQV}
        onOpenChange={(o) => { if (!o) setTxnQV(null); }}
        onEdit={(t) => { setEditing(t); setDialogOpen(true); }}
        onDelete={(t) => setConfirmTarget(t)}
        onDonorClick={(id) => { setTxnQV(null); setDonorQV(id); }}
      />
    </div>
  );
}

/** Total-summary card with a Popover breakdown of the top 5 contributing rows. */
function BreakdownCard({ testid, label, value, valueClass, subline, rows, altRows, altLabel, rowLabel, empty }) {
  const [showAlt, setShowAlt] = useState(false);
  const visible = showAlt && altRows ? altRows : rows;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="p-5 border-0 shadow-sm bg-card rounded-3xl text-left hover:bg-secondary/30 transition-colors group cursor-pointer w-full"
          data-testid={`${testid}-card`}
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
            <Calculator className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
          </div>
          <div className={`font-serif text-3xl mt-2 tabular-nums ${valueClass}`} data-testid={testid}>{formatUSD(value)}</div>
          <div className="text-xs text-muted-foreground mt-1">{subline}</div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 rounded-2xl p-4" align="start">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{showAlt ? altLabel : rowLabel}</div>
          {altRows && (
            <button className="text-[11px] text-primary hover:underline" onClick={() => setShowAlt(!showAlt)} data-testid={`${testid}-toggle`}>
              Show {showAlt ? rowLabel : altLabel}
            </button>
          )}
        </div>
        {visible.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">{empty}</div>
        ) : (
          <div className="space-y-2" data-testid={`${testid}-breakdown`}>
            {visible.map((r, i) => {
              const pct = value ? Math.round((r.total / value) * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm">
                    <span className="truncate">{r.name}</span>
                    <span className="font-mono tabular-nums">{formatUSD(r.total)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary mt-1 overflow-hidden">
                    <div className={`h-full ${valueClass.replace("text-", "bg-")}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{pct}%</div>
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
